/**
 * The physical row and the write-then-verify path.
 *
 * A row is `{ key, schemaVersion, payload, index }` where `payload` is
 * **canonical JSON text**, not a structured-cloned object graph. Three reasons,
 * none of them aesthetic:
 *
 *  1. **Write verification only works on bytes.** `verifyWrite` compares
 *     strings. Comparing object graphs would compare key order, `undefined` vs
 *     absent, and numeric coercion — where every difference is harmless, and a
 *     harmless difference is exactly what makes verification stop catching real
 *     corruption.
 *  2. **Byte preservation requires bytes.** A record that will not decode is
 *     still a valid string. It stays exactly where it is, unmodified, and its
 *     length is what `preservedByteLength` reports. No separate quarantine
 *     store, and nothing is moved during a failure.
 *  3. **Content hashing depends on determinism**, which an object graph does
 *     not have.
 *
 * The verification here re-*decodes* what was written and re-*encodes* it,
 * rather than comparing the stored text to itself. Comparing the text to itself
 * would only catch a storage layer that mangled a string — which does not
 * happen — while re-decoding catches the failures that do: a payload this build
 * can no longer read back, a lossy decoder, an index or version that did not
 * land with the payload it was derived from. F-JP-20 states the same rule for
 * the native store: every caller re-decodes the persisted record and compares
 * canonical bytes, and returns the re-decoded value rather than the input.
 */

import type { FailClosedTrigger, SchemaVersion, WriteOutcome } from "@ff/domain";
import { schemaVersion as brandSchemaVersion, verifyWrite } from "@ff/domain";
import type { RecordCodec, RecordKey, RecordMutation } from "@ff/domain/ports";

import { canonicalJson } from "./canonicalJson";
import type { RecordStoreLayout } from "./layout";
import { resolveRecordKey } from "./layout";

/**
 * Absence has to have a text form, because verification is defined over
 * canonical text and a removal is verified exactly as a write is — a delete
 * that silently left its row behind must be a failure, not a success the caller
 * believes in.
 */
const ABSENT = "";

/** Scalars an index may hold. Arrays exist for `multiEntry` tag indexes. */
export type RecordIndexValue =
  | string
  | number
  | Date
  | readonly (string | number | Date)[];

/**
 * The materialized scalars, keyed by **bare field name**. They are nested under
 * the row's `index` property, and the layout's key paths address them from
 * there (`index.startedAt`), so a field here is named `startedAt` and not
 * `index.startedAt` — a literal dotted key would be a property IndexedDB's key
 * path could never reach, and the index would silently stay empty.
 */
export type RecordIndexFields = Readonly<Record<string, RecordIndexValue>>;

/**
 * Derives the indexed scalars from the record's decoded value. Runs in the same
 * step as the encode, so an index can never describe a payload it was not
 * derived from.
 */
export type RecordIndexer<T> = (value: T) => RecordIndexFields;

export interface StoredRow {
  readonly key: RecordKey;
  readonly schemaVersion: number;
  readonly payload: string;
  readonly index: RecordIndexFields;
}

/** The result of looking at one raw row IndexedDB handed back. */
export type StoredRowDecode =
  | { readonly status: "row"; readonly row: StoredRow }
  | {
      readonly status: "unusable";
      readonly trigger: FailClosedTrigger;
      readonly detail: string;
      readonly observedSchemaVersion: SchemaVersion | null;
      readonly preservedByteLength: number;
    };

/**
 * The minimum of an IndexedDB object store this module needs. Declared
 * structurally rather than imported from `idb` so the write path can be
 * exercised — and read — without a database standing behind it.
 */
export interface RecordObjectStore {
  get(key: IDBValidKey): Promise<unknown>;
  put(value: unknown, key?: IDBValidKey): Promise<IDBValidKey>;
  delete(key: IDBValidKey): Promise<void>;
  getAll(): Promise<unknown[]>;
  getAllKeys(): Promise<IDBValidKey[]>;
}

/** One mutation reduced to bytes, before any transaction is opened. */
export interface PlannedMutation {
  readonly key: RecordKey;
  /** `null` for a removal: the record must read back absent. */
  readonly row: StoredRow | null;
  /** The canonical text the read-back has to equal. `""` means "absent". */
  readonly expectedCanonical: string;
}

export function canonicalRow(row: StoredRow): string {
  return canonicalJson(row);
}

/**
 * Reads one raw row without trusting it. Anything IndexedDB can hold may be
 * sitting here — a row written by a future build, a row half-written by a
 * browser that crashed mid-commit — and the only safe assumption is that it is
 * `unknown`.
 */
export function decodeStoredRow(raw: unknown, key: RecordKey): StoredRowDecode {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return unusable("unsupportedShape", "row is not an object", null, raw);
  }
  const row = raw as Record<string, unknown>;

  const payload = row["payload"];
  if (typeof payload !== "string") {
    // The payload is the record. Without text there is nothing to decode, and
    // nothing to preserve a length of beyond the row itself.
    return unusable("corruptBytes", "row payload is not text", null, raw);
  }

  const version = row["schemaVersion"];
  if (typeof version !== "number" || !Number.isInteger(version)) {
    return unusable(
      "unsupportedShape",
      "row has no integer schemaVersion",
      null,
      raw,
    );
  }

  const indexFields = row["index"];
  const index =
    indexFields === null || typeof indexFields !== "object" || Array.isArray(indexFields)
      ? {}
      : (indexFields as RecordIndexFields);

  const rowKey = row["key"];
  return {
    status: "row",
    row: {
      // An out-of-line singleton row has no `key` field of its own; its key is
      // the layout's literal, which the caller already knows.
      key: typeof rowKey === "string" ? rowKey : key,
      schemaVersion: version,
      payload,
      index,
    },
  };
}

/**
 * Turns mutations into rows and the exact text each one must read back as.
 *
 * Deliberately runs **before** a transaction is opened. Encoding is where a
 * value that cannot be canonically represented is discovered, and discovering
 * it with a write transaction already open would mean aborting a transaction
 * that never needed to exist.
 *
 * Throws {@link CanonicalJsonError} — the caller turns that into a refusal,
 * because "this value has no canonical form" is a fact about the value, not a
 * fact about the store.
 */
export function planMutations<T>(
  layout: RecordStoreLayout,
  codec: RecordCodec<T>,
  version: SchemaVersion,
  indexer: RecordIndexer<T>,
  mutations: readonly RecordMutation<T>[],
): readonly PlannedMutation[] {
  const planned: PlannedMutation[] = [];
  for (const mutation of mutations) {
    const key = resolveRecordKey(layout, mutation.key);
    if (mutation.kind === "remove") {
      planned.push({ key, row: null, expectedCanonical: ABSENT });
      continue;
    }
    const row: StoredRow = {
      key,
      schemaVersion: version,
      payload: codec.encode({ schemaVersion: version, payload: mutation.value }),
      index: indexer(mutation.value),
    };
    planned.push({ key, row, expectedCanonical: canonicalRow(row) });
  }
  return planned;
}

/**
 * Writes every planned mutation and verifies each one by re-reading it **inside
 * the same transaction**.
 *
 * Returns the first failure and stops. It does not undo what it already wrote:
 * the caller aborts the transaction, which is what makes the batch atomic.
 * Committing the rest would leave a partial mutation the caller has no way to
 * describe — a `WriteOutcome` cannot say "three of five".
 */
export async function applyVerifiedMutations<T>(
  store: RecordObjectStore,
  layout: RecordStoreLayout,
  codec: RecordCodec<T>,
  planned: readonly PlannedMutation[],
): Promise<WriteOutcome> {
  const inline = layout.keying.kind === "inline";

  for (const mutation of planned) {
    if (mutation.row === null) {
      await store.delete(mutation.key);
      continue;
    }
    // An inline store reads the key out of the row; passing one explicitly to a
    // store with a key path is a `DataError`, and passing none to an
    // out-of-line store is how a second unkeyed record would appear.
    if (inline) {
      await store.put(mutation.row);
    } else {
      await store.put(mutation.row, mutation.key);
    }
  }

  for (const mutation of planned) {
    const outcome = await verifyMutation(store, codec, mutation);
    if (outcome.status !== "verified") return outcome;
  }

  return { status: "verified" };
}

async function verifyMutation<T>(
  store: RecordObjectStore,
  codec: RecordCodec<T>,
  mutation: PlannedMutation,
): Promise<WriteOutcome> {
  const raw = await store.get(mutation.key);

  if (mutation.row === null) {
    return verifyWrite(
      ABSENT,
      raw === undefined ? ABSENT : bestEffortCanonical(raw),
    );
  }

  if (raw === undefined) {
    return verifyWrite(mutation.expectedCanonical, null);
  }

  const row = decodeStoredRow(raw, mutation.key);
  if (row.status === "unusable") {
    return verifyWrite(mutation.expectedCanonical, null);
  }

  const decoded = codec.decode(row.row.payload);
  if (decoded.status === "undecodable") {
    // Written, and already unreadable. Reporting `verified` here would hand the
    // caller a success over a record the very next read will refuse.
    return verifyWrite(mutation.expectedCanonical, null);
  }

  // Re-encode from the *decoded* value rather than reusing the stored text, so
  // a decoder that quietly drops a field fails here instead of at the next read.
  const reEncoded = codec.encode({
    schemaVersion: brandSchemaVersion(row.row.schemaVersion),
    payload: decoded.value,
  });
  return verifyWrite(
    mutation.expectedCanonical,
    canonicalRow({ ...row.row, payload: reEncoded }),
  );
}

function unusable(
  trigger: FailClosedTrigger,
  detail: string,
  observedSchemaVersion: SchemaVersion | null,
  raw: unknown,
): StoredRowDecode {
  return {
    status: "unusable",
    trigger,
    detail,
    observedSchemaVersion,
    preservedByteLength: bestEffortCanonical(raw).length,
  };
}

/**
 * A length and a description for bytes that are not in canonical form. Used
 * only to report what was found — never to write anything back.
 */
function bestEffortCanonical(raw: unknown): string {
  try {
    return canonicalJson(raw);
  } catch {
    return String(raw);
  }
}
